"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useEffect } from "react";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiWave } from "@/generated/models/ApiWave";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { useLayout } from "./layout/LayoutContext";
import { useWaveDropsLeaderboard, WaveDropsLeaderboardSort } from "@/hooks/useWaveDropsLeaderboard";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import ParticipationDrop from "@/components/waves/drops/participation/ParticipationDrop";
import { DropLocation } from "@/components/waves/drops/drop.types";
import { WaveLeaderboardLoading } from "@/components/waves/leaderboard/drops/WaveLeaderboardLoading";
import { WaveLeaderboardLoadingBar } from "@/components/waves/leaderboard/drops/WaveLeaderboardLoadingBar";
import { useApprovalWaveStatus } from "@/hooks/waves/useApprovalWaveStatus";
const CURATION_FILTER_PARAM = "curation_id";
interface MyStreamWaveSubmissionsProps {
  readonly wave: ApiWave;
  readonly onDropClick: (drop: ExtendedDrop) => void;
}
const MyStreamWaveSubmissions: React.FC<MyStreamWaveSubmissionsProps> = t0 => {
  const $ = _c(53);
  const {
    wave,
    onDropClick
  } = t0;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams.toString();
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const searchParamsString = t1;
  const {
    leaderboardViewStyle
  } = useLayout();
  let t2;
  if ($[2] !== wave) {
    t2 = {
      wave
    };
    $[2] = wave;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    winningThreshold,
    isVotingClosed,
    isVotingControlsLocked
  } = useApprovalWaveStatus(t2);
  let t3;
  if ($[4] !== wave.id) {
    t3 = {
      waveId: wave.id,
      sort: WaveDropsLeaderboardSort.RANK,
      curatedByGroupId: undefined
    };
    $[4] = wave.id;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    drops,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetching,
    isFetchingNextPage,
    refetch
  } = useWaveDropsLeaderboard(t3);
  const handleReply = _temp;
  let t4;
  if ($[6] !== refetch) {
    t4 = () => {
      refetch();
    };
    $[6] = refetch;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleRetry = t4;
  let t5;
  let t6;
  if ($[8] !== pathname || $[9] !== router || $[10] !== searchParamsString) {
    t5 = () => {
      const params = new URLSearchParams(searchParamsString || "");
      if (!params.has(CURATION_FILTER_PARAM)) {
        return;
      }
      params.delete(CURATION_FILTER_PARAM);
      const nextQuery = params.toString();
      const nextUrl = nextQuery ? `${pathname}?${nextQuery}` : pathname;
      router.replace(nextUrl, {
        scroll: false
      });
    };
    t6 = [pathname, router, searchParamsString];
    $[8] = pathname;
    $[9] = router;
    $[10] = searchParamsString;
    $[11] = t5;
    $[12] = t6;
  } else {
    t5 = $[11];
    t6 = $[12];
  }
  useEffect(t5, t6);
  let t7;
  if ($[13] !== pathname || $[14] !== router || $[15] !== searchParamsString) {
    t7 = dropId => {
      const params_0 = new URLSearchParams(searchParamsString || "");
      params_0.delete(CURATION_FILTER_PARAM);
      params_0.set("drop", dropId);
      router.push(`${pathname}?${params_0.toString()}`, {
        scroll: false
      });
    };
    $[13] = pathname;
    $[14] = router;
    $[15] = searchParamsString;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const openDropById = t7;
  let t8;
  if ($[17] !== openDropById) {
    t8 = drop => {
      openDropById(drop.id);
    };
    $[17] = openDropById;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const handleQuoteClick = t8;
  let t9;
  if ($[19] !== fetchNextPage || $[20] !== hasNextPage || $[21] !== isFetching || $[22] !== isFetchingNextPage) {
    t9 = isIntersecting => {
      if (!isIntersecting || !hasNextPage || isFetching || isFetchingNextPage) {
        return;
      }
      fetchNextPage();
    };
    $[19] = fetchNextPage;
    $[20] = hasNextPage;
    $[21] = isFetching;
    $[22] = isFetchingNextPage;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const handleIntersection = t9;
  const intersectionElementRef = useIntersectionObserver(handleIntersection);
  let content;
  if (isError && drops.length === 0) {
    let t10;
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <p className="tw-mb-0 tw-text-sm tw-text-iron-300">Unable to load submissions.</p>;
      $[24] = t10;
    } else {
      t10 = $[24];
    }
    let t11;
    if ($[25] !== handleRetry) {
      t11 = <div className="tw-mt-10 tw-flex tw-flex-col tw-items-center tw-gap-4 tw-text-center">{t10}<button type="button" onClick={handleRetry} className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-iron-500 tw-bg-transparent tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-iron-100 tw-transition-colors focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-iron-300 desktop-hover:hover:tw-bg-iron-800">Try again</button></div>;
      $[25] = handleRetry;
      $[26] = t11;
    } else {
      t11 = $[26];
    }
    content = t11;
  } else {
    if (isFetching && drops.length === 0) {
      let t10;
      if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
        t10 = <WaveLeaderboardLoading />;
        $[27] = t10;
      } else {
        t10 = $[27];
      }
      content = t10;
    } else {
      if (drops.length === 0) {
        let t10;
        if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
          t10 = <div className="tw-mt-10"><p className="tw-text-center tw-text-sm tw-text-iron-500">No submissions to show.</p></div>;
          $[28] = t10;
        } else {
          t10 = $[28];
        }
        content = t10;
      } else {
        let t10;
        if ($[29] !== drops || $[30] !== handleQuoteClick || $[31] !== isVotingClosed || $[32] !== isVotingControlsLocked || $[33] !== onDropClick || $[34] !== winningThreshold) {
          let t11;
          if ($[36] !== handleQuoteClick || $[37] !== isVotingClosed || $[38] !== isVotingControlsLocked || $[39] !== onDropClick || $[40] !== winningThreshold) {
            t11 = drop_0 => <ParticipationDrop key={drop_0.id} drop={drop_0} showWaveInfo={false} activeDrop={null} showReplyAndQuote={false} location={DropLocation.WAVE} onReply={handleReply} onQuoteClick={handleQuoteClick} onDropContentClick={onDropClick} winningThreshold={winningThreshold} isVotingClosed={isVotingClosed} isVotingControlsLocked={isVotingControlsLocked} />;
            $[36] = handleQuoteClick;
            $[37] = isVotingClosed;
            $[38] = isVotingControlsLocked;
            $[39] = onDropClick;
            $[40] = winningThreshold;
            $[41] = t11;
          } else {
            t11 = $[41];
          }
          t10 = drops.map(t11);
          $[29] = drops;
          $[30] = handleQuoteClick;
          $[31] = isVotingClosed;
          $[32] = isVotingControlsLocked;
          $[33] = onDropClick;
          $[34] = winningThreshold;
          $[35] = t10;
        } else {
          t10 = $[35];
        }
        let t11;
        if ($[42] !== isFetchingNextPage) {
          t11 = isFetchingNextPage && <WaveLeaderboardLoadingBar />;
          $[42] = isFetchingNextPage;
          $[43] = t11;
        } else {
          t11 = $[43];
        }
        let t12;
        if ($[44] !== intersectionElementRef) {
          t12 = <div ref={intersectionElementRef} />;
          $[44] = intersectionElementRef;
          $[45] = t12;
        } else {
          t12 = $[45];
        }
        let t13;
        if ($[46] !== t10 || $[47] !== t11 || $[48] !== t12) {
          t13 = <div className="tw-mt-4 tw-space-y-2">{t10}{t11}{t12}</div>;
          $[46] = t10;
          $[47] = t11;
          $[48] = t12;
          $[49] = t13;
        } else {
          t13 = $[49];
        }
        content = t13;
      }
    }
  }
  let t10;
  if ($[50] !== content || $[51] !== leaderboardViewStyle) {
    t10 = <div className="tw-space-y-4 tw-overflow-y-auto tw-px-2 tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 hover:tw-scrollbar-thumb-iron-300 sm:tw-px-4 lg:tw-space-y-6" style={leaderboardViewStyle}>{content}</div>;
    $[50] = content;
    $[51] = leaderboardViewStyle;
    $[52] = t10;
  } else {
    t10 = $[52];
  }
  return t10;
};
export default MyStreamWaveSubmissions;
function _temp() {}